import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import Fuse from 'fuse.js';
import noImage from '../assets/img/brand/noImage.png';

export default function MultiSelectSearch({
  label,
  tooltip,
  options = [],
  selected = [],
  setSelected,
  keysToSearch = ['name'],
  idKey = 'id',
  displayKey = 'name',
  subTitleKey = null,
  logoKey = 'logo',
  multiple = true,
  currentBrandId = null,
  fallbackImage = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const safeSelected = Array.isArray(selected) ? selected : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-focus input when open
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const fuse = new Fuse(
    options.filter(o => !o.is_archived && Number(o[idKey]) !== Number(currentBrandId)),
    { keys: keysToSearch, threshold: 0.3, includeMatches: true }
  );

  const filteredOptions = search
    ? fuse.search(search)
    : options.filter(o => !o.is_archived && Number(o[idKey]) !== Number(currentBrandId)).map(item => ({ item }));

  const highlightMatch = (text, indices) => {
    if (!indices) return text;
    let result = [];
    let lastIndex = 0;
    indices.forEach(([start, end]) => {
      if (start > lastIndex) result.push(text.slice(lastIndex, start));
      result.push(
        // <span key={start} style={{ color: '#0d6efd' }}>
        <span key={start}>
          {text.slice(start, end + 1)}
        </span>
      );
      lastIndex = end + 1;
    });
    if (lastIndex < text.length) result.push(text.slice(lastIndex));
    return result;
  };

  const handleSelect = (item) => {
    if (multiple) {
      if (!safeSelected.some(s => s[idKey] === item[idKey])) {
        setSelected([...safeSelected, item]);
      }
    } else {
      setSelected([item]);
    }
    setSearch('');
    setIsOpen(false);
  };

  const handleRemove = (id) => {
    if (multiple) {
      setSelected(safeSelected.filter(s => s[idKey] !== id));
    } else {
      setSelected([]);
    }
  };

  return (
    <div className="multi-select" ref={containerRef} style={{ position: 'relative' }}>
      <label htmlFor="label" className="form-label d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
        {label}
        {tooltip}
      </label>

      <div
        className="select-box form-control"
        onClick={() => setIsOpen(true)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          // gap: '6px',
          alignItems: 'center',
          cursor: safeSelected.length && !multiple ? 'default' : 'text',
          paddingRight: !multiple && safeSelected.length ? '4px' : '4px',
          border: '1px solid #6C757D'
        }}
      >
        {multiple
          ? safeSelected.map(item => (
            <div
              key={item[idKey]}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                background: '#f1f3f5',
                borderRadius: '6px',
                padding: '2px 6px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {item[logoKey] && (
                  <img
                    src={item[logoKey]}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'contain' }}
                  />
                )}
                <span style={{
                  fontFamily: "Montserrat",
                  color: "#181818",
                  fontSize: " 14px",
                  fontWeight: "400",
                  lineHeight: "18px",
                }}>{item[displayKey]}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemove(item[idKey]); }}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))
          : safeSelected[0] && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', }}>
                {safeSelected[0][logoKey] && (
                  <img
                    src={safeSelected[0][logoKey]}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'contain' }}
                  />
                )}
                <span style={{
                  fontFamily: "Montserrat",
                  color: "#181818",
                  fontSize: " 14px",
                  fontWeight: "400",
                  lineHeight: "18px",
                }}>{safeSelected[0][displayKey]}</span>
              </div>
            </div>
          )}

        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            if (multiple || !safeSelected.length) setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          style={{ border: 'none', flex: 1, outline: 'none', minHeight: '24px', background: 'transparent' }}
          disabled={!multiple && safeSelected.length > 0}
        />

        {/* Single select X button */}
        {!multiple && safeSelected.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleRemove(safeSelected[0][idKey]); setSearch(''); }}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
          >
            {/* <X size={18} color="#000" /> */}
          </button>
        )}

        {/* Dropdown arrow */}
        {/* <div
          style={{
            // background: '#6C757D',
            borderRadius: 4,
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: '0.2s',
          }}
        >
          <ChevronDown
            size={18}
            style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: '#6C757D', transition: '0.2s' }}
          />
        </div> */}



        {/* Icons area */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {/* X icon ONLY when a value is selected */}
          {!multiple && safeSelected.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(safeSelected[0][idKey]);
                setSearch('');
              }}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px',
              }}
              title="Clear"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M9 3L3 9M3 3L9 9"
                  stroke="#212529"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* Dropdown Arrow Icon */}
          <div
            style={{
              borderRadius: 4,
              // padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: '0.2s',
              cursor: 'pointer',
              marginRight: '7px',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              style={{
                transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: '0.2s',
              }}
            >
              <path
                d="M4.5 11.25L9 6.75L13.5 11.25"
                stroke="#212529"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

      </div>

      {isOpen && (
        <div
          className="dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '6px',
            maxHeight: '250px',
            overflowY: 'auto',
            zIndex: 1000
          }}
        >
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filteredOptions.map(({ item, matches }) => {
              const match = matches?.find(m => keysToSearch.includes(m.key));
              return (
                <li
                  key={item[idKey]}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHoveredItem(item[idKey])}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 2,
                    backgroundColor: hoveredItem === item[idKey] ? '#f0f0f0' : 'transparent',
                    borderRadius: 4,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                    <img
                      src={item[logoKey] || noImage}
                      alt={item[displayKey]}
                      // onError={(e) => (e.target.src = noImage)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        objectFit: 'contain',
                        background: '#f5f5f5',
                      }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{highlightMatch(item[displayKey], match?.indices)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
            {filteredOptions.length === 0 && (
              <li style={{ textAlign: 'center', padding: '8px', color: '#999' }}>No results found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
